import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { getLoggedInStudentId } from '../utils/sessionUser';

const SEND_MESSAGE_LOGIN_ERROR =
  'You must be logged in to send a message.';
const SEND_MESSAGE_GENERIC_ERROR =
  'There was an issue sending a notification, please try again.';

function showSendMessageError(setStatus, setStatusMessage, detail) {
  setStatus('error');
  setStatusMessage(
    detail === SEND_MESSAGE_LOGIN_ERROR
      ? SEND_MESSAGE_LOGIN_ERROR
      : SEND_MESSAGE_GENERIC_ERROR
  );
}

export default function SendNotificationsPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [students, setStudents] = useState([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [linkText, setLinkText] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const loggedInId = currentUser?.id;

  useEffect(() => {
    const load = async () => {
      let user = route?.params?.user;
      const uid =
        route?.params?.userId ??
        user?.id ??
        (await getLoggedInStudentId());
      if (uid && !user) {
        try {
          const res = await fetch(`${getApiUrl()}/api/users/${uid}`);
          if (res.ok) user = await res.json();
        } catch {
          /* ignore */
        }
      }
      if (user) setCurrentUser(user);
      fetchStudents(user?.id ?? uid);
    };
    void load();
  }, [route?.params?.user, route?.params?.userId]);

  const fetchStudents = async (excludeId) => {
    setLoadingStudents(true);
    try {
      const requesterId = excludeId ?? user?.id;
      const requesterQ =
        requesterId != null
          ? `&requester_id=${encodeURIComponent(String(requesterId))}`
          : '';
      const res = await fetch(`${getApiUrl()}/api/users?visible=true${requesterQ}`);
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const selfId = Number(excludeId);
        setStudents(
          list.filter((s) => {
            if (!Number.isFinite(selfId)) return true;
            return Number(s.id) !== selfId;
          })
        );
      }
    } catch (e) {
      console.error('Error fetching students:', e);
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const clearForm = () => {
    setSelectedRecipientId(null);
    setShowStudentDropdown(false);
    setMessageText('');
    setLinkText('');
    setStatus(null);
    setStatusMessage('');
  };

  const getRecipientLabel = () => {
    if (!selectedRecipientId) return 'Select student…';
    const s = students.find((x) => x.id === selectedRecipientId);
    return s?.student_name || s?.username || 'Selected student';
  };

  const handleSend = async () => {
    if (!selectedRecipientId) {
      showSendMessageError(setStatus, setStatusMessage, 'validation');
      return;
    }
    if (!messageText.trim()) {
      showSendMessageError(setStatus, setStatusMessage, 'validation');
      return;
    }
    if (!loggedInId) {
      showSendMessageError(setStatus, setStatusMessage, SEND_MESSAGE_LOGIN_ERROR);
      return;
    }

    setSending(true);
    setStatus(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedRecipientId,
          text: messageText.trim(),
          link: linkText.trim() || null,
          created_by: loggedInId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showSendMessageError(setStatus, setStatusMessage, data.error);
        return;
      }
      setStatus('success');
      setStatusMessage('Notification sent. The recipient will see it in their bell icon.');
      setMessageText('');
      setLinkText('');
      setSelectedRecipientId(null);
    } catch (e) {
      showSendMessageError(setStatus, setStatusMessage, e.message);
    } finally {
      setSending(false);
    }
  };

  const styles = getStyles(theme);

  return (
    <View
      style={styles.container}
      data-screen="send-notifications"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Send message</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <View style={styles.titleSection}>
          <Text style={styles.title}>Send message</Text>
          <Text style={styles.subtitle}>
            Send a notification to another student. They can open it from the bell icon on any
            page. Add an optional link to open a screen when they tap the message.
          </Text>
        </View>

        {status ? (
          <View
            nativeID={
              status === 'success'
                ? 'send-message-status-toast-success'
                : 'send-message-status-toast-error'
            }
            style={[
              styles.statusBox,
              status === 'success' ? styles.successBox : styles.errorBox,
            ]}
          >
            <Text
              style={[
                styles.statusBoxTitle,
                status === 'success' ? styles.successBoxText : styles.errorBoxText,
              ]}
            >
              {status === 'success' ? 'Sent' : 'Could not send'}
            </Text>
            <Text
              style={[
                styles.statusMessage,
                status === 'success' ? styles.successBoxText : styles.errorBoxText,
              ]}
            >
              {statusMessage}
            </Text>
            <View style={styles.statusOkFooter} nativeID="send-message-status-ok-footer">
              <TouchableOpacity
                style={styles.statusOkButton}
                nativeID="send-message-status-ok-button"
                onPress={() => {
                  setStatus(null);
                  setStatusMessage('');
                }}
                accessibilityRole="button"
                accessibilityLabel="OK"
              >
                <Text style={styles.statusOkButtonText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}

        <View style={styles.form}>
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Student *</Text>
            <Text style={styles.helpText}>Visible students except you</Text>
            <TouchableOpacity
              style={styles.dropdownTrigger}
              nativeID="send-message-student-dropdown-trigger"
              onPress={() => setShowStudentDropdown((v) => !v)}
              disabled={loadingStudents}
              {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
            >
              {loadingStudents ? (
                <ActivityIndicator size="small" color={theme.colors.heading} />
              ) : (
                <>
                  <Text
                    style={[
                      styles.dropdownTriggerText,
                      !selectedRecipientId && styles.dropdownPlaceholder,
                    ]}
                    numberOfLines={1}
                  >
                    {getRecipientLabel()}
                  </Text>
                  <Text style={styles.dropdownArrow}>{showStudentDropdown ? '▲' : '▼'}</Text>
                </>
              )}
            </TouchableOpacity>
            {showStudentDropdown && students.length > 0 ? (
              <ScrollView
                style={styles.dropdownList}
                nativeID="send-message-student-dropdown-list"
                {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
              >
                {students.map((s) => (
                  <TouchableOpacity
                    key={s.id}
                    style={[
                      styles.dropdownListItem,
                      selectedRecipientId === s.id && styles.dropdownListItemSelected,
                    ]}
                    onPress={() => {
                      setSelectedRecipientId(s.id);
                      setShowStudentDropdown(false);
                    }}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text style={styles.dropdownListItemText}>
                      {s.student_name || s.username}
                      {s.student_grade ? ` · ${s.student_grade}` : ''}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            ) : null}
            {showStudentDropdown && !loadingStudents && students.length === 0 ? (
              <Text style={styles.emptyHint}>No other visible students found.</Text>
            ) : null}
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Message *</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={messageText}
              onChangeText={setMessageText}
              placeholder="Notification text"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
              editable={!sending}
            />
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>(optional) link</Text>
            <Text style={styles.helpText}>Copy the URL and paste here if relevant.</Text>
            <TextInput
              style={styles.input}
              value={linkText}
              onChangeText={setLinkText}
              placeholder="Deep link or URL"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              autoCapitalize="none"
              autoCorrect={false}
              editable={!sending}
            />
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.clearButton]}
              onPress={clearForm}
              disabled={sending}
            >
              <Text style={styles.clearButtonText}>Clear</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.sendButton, sending && styles.buttonDisabled]}
              onPress={() => void handleSend()}
              disabled={sending}
            >
              {sending ? (
                <ActivityIndicator color="#000000" />
              ) : (
                <Text style={styles.sendButtonText}>Send</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
    },
    hamburgerContainer: {
      marginRight: 15,
    },
    breadcrumbContainer: {
      flex: 1,
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
    },
    titleSection: {
      marginBottom: 28,
      alignItems: 'center',
    },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      marginBottom: 10,
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    subtitle: {
      fontSize: 14,
      fontStyle: 'italic',
      color: theme.colors.text,
      textAlign: 'center',
      paddingHorizontal: 12,
      lineHeight: 20,
      opacity: 0.85,
    },
    form: {
      width: '100%',
      maxWidth: 600,
      alignSelf: 'center',
    },
    fieldContainer: {
      marginBottom: 20,
      zIndex: 1,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 6,
    },
    helpText: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginBottom: 8,
      fontStyle: 'italic',
    },
    input: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.border,
      ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    textArea: {
      minHeight: 140,
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.isDarkMode ? theme.colors.inputBackground : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      borderWidth: 1,
      borderColor: theme.colors.border,
      minHeight: 48,
    },
    dropdownTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    dropdownPlaceholder: {
      opacity: 0.55,
      backgroundColor: 'transparent',
    },
    dropdownArrow: {
      fontSize: 14,
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    dropdownList: {
      marginTop: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.inputBackground : '#F5F5F5',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      maxHeight: 220,
    },
    dropdownListItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownListItemSelected: {
      backgroundColor: theme.isDarkMode
        ? 'rgba(253, 253, 10, 0.12)'
        : 'rgba(212, 175, 55, 0.15)',
    },
    dropdownListItemText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    emptyHint: {
      marginTop: 8,
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.7,
      fontStyle: 'italic',
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 24,
      gap: 15,
    },
    button: {
      flex: 1,
      padding: 15,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 50,
    },
    buttonDisabled: {
      opacity: 0.7,
    },
    clearButton: {
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E0E0E0',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    clearButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
    },
    sendButton: {
      backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    },
    sendButtonText: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.isDarkMode ? '#000000' : '#FFFFFF',
    },
    statusBox: {
      borderRadius: 8,
      paddingTop: 16,
      paddingHorizontal: 16,
      paddingBottom: 52,
      marginBottom: 20,
      maxWidth: 600,
      alignSelf: 'center',
      width: '100%',
      position: 'relative',
    },
    successBox: {
      backgroundColor: theme.isDarkMode ? '#1b3d1f' : '#e8f5e9',
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#2e7d32' : '#81c784',
    },
    errorBox: {
      backgroundColor: theme.isDarkMode ? '#3d1b1b' : '#DC3545',
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#c62828' : '#9a1b1f',
    },
    statusBoxTitle: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 6,
    },
    successBoxText: {
      color: theme.isDarkMode ? '#a5d6a7' : '#2e7d32',
    },
    errorBoxText: {
      color: theme.isDarkMode ? '#ef9a9a' : '#FFFFFF',
    },
    statusMessage: {
      fontSize: 14,
      lineHeight: 20,
    },
    statusOkFooter: {
      position: 'absolute',
      right: 12,
      bottom: 10,
      backgroundColor: 'transparent',
    },
    statusOkButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
      paddingVertical: 10,
      paddingHorizontal: 22,
      borderRadius: 8,
      minWidth: 72,
      alignItems: 'center',
    },
    statusOkButtonText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '700',
    },
  });
